import React, { useState, useEffect } from 'react'
import { Loader } from "@googlemaps/js-api-loader"

import { useAxios } from "../../hooks/useAxios";
import redcross from '../../assets/redcross.png'
import camp_png from '../../assets/camp.png'
import './maps.scss'

import hosueholds from '../../assets/households.png'
import tracking_icon from '../../assets/tracking.png'
import shiftin from '../../assets/shiftin.png'
import shiftout from '../../assets/shiftout.png'

import cataract from "../../assets/cataractt.png"
import diabetes from "../../assets/diabetes.png";
import hypertension from "../../assets/hypertension.jpg";
import cataractDia from "../../assets/cataract_diabetes.png";
import cataractHyper from "../../assets/cataract_hypertension.png";
import HyperDia from "../../assets/hyper_diabetes.png"
import cataractHyperDia from "../../assets/cataract_hyper_diabetes.png"

import red from '../../assets/red.png'
import violet from '../../assets/violet.png'
import green from '../../assets/green.png'
import orange from '../../assets/orange.png'
import lightblue from '../../assets/lightblue.png'
import brown from '../../assets/brown.png'
import purple from '../../assets/purple.png'
import yellow from '../../assets/yellow.png'

import logo from "../../assets/SankaraLogo.png";
import { useNavigate } from "react-router-dom";

const loader = new Loader({
    apiKey: "AIzaSyDoxlT05aCQHJP_Ni5tkXixB3JnYj6ltPY",
    version: "weekly",
    libraries: ['visualization'],
})

// ─── Coordinate helpers ──────────────────────────────────────────────────────

function toNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function isValidCoordinate(lat, lng) {
    if (lat === null || lng === null) return false;
    if (lat === 0 && lng === 0) return false;
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

function normalizeCoordinate(latValue, lngValue) {
    const lat = toNumber(latValue);
    const lng = toNumber(lngValue);

    if (isValidCoordinate(lat, lng)) {
        return { lat, lng };
    }
    // Some records arrive with swapped lat/lng — try the swap
    if (isValidCoordinate(lng, lat)) {
        return { lat: lng, lng: lat };
    }
    return null;
}

// ─── Cluster / zoom helpers ───────────────────────────────────────────────────

function getDensestClusterPoints(points, bucketSize = 0.05) {
    if (!Array.isArray(points) || points.length <= 2) return points || [];

    const buckets = new Map();
    points.forEach((point) => {
        const latBucket = Math.floor(point.lat / bucketSize);
        const lngBucket = Math.floor(point.lng / bucketSize);
        const key = `${latBucket}:${lngBucket}`;
        if (!buckets.has(key)) buckets.set(key, { count: 0, latBucket, lngBucket });
        buckets.get(key).count += 1;
    });

    let densest = null;
    buckets.forEach((bucket) => {
        if (!densest || bucket.count > densest.count) densest = bucket;
    });

    if (!densest) return points;

    const clustered = points.filter((point) => {
        const latBucket = Math.floor(point.lat / bucketSize);
        const lngBucket = Math.floor(point.lng / bucketSize);
        return (
            Math.abs(latBucket - densest.latBucket) <= 1 &&
            Math.abs(lngBucket - densest.lngBucket) <= 1
        );
    });

    return clustered.length > 0 ? clustered : points;
}

function focusMapOnDenseArea(mapRef, googleRef, points, options = {}) {
    const {
        bucketSize = 0.02,
        minClusterPoints = 8,
        padding = 40,
        minZoom = 12,
        maxZoom = 17,
    } = options;

    const validPoints = (points || []).filter((p) =>
        isValidCoordinate(p?.lat, p?.lng)
    );

    if (validPoints.length === 0) return false;

    const viewportPoints =
        validPoints.length >= minClusterPoints
            ? getDensestClusterPoints(validPoints, bucketSize)
            : validPoints;

    const bounds = new googleRef.maps.LatLngBounds();
    viewportPoints.forEach((p) =>
        bounds.extend(new googleRef.maps.LatLng(p.lat, p.lng))
    );

    if (bounds.isEmpty()) return false;

    mapRef.fitBounds(bounds, padding);
    googleRef.maps.event.addListenerOnce(mapRef, "bounds_changed", () => {
        if (mapRef.getZoom() > maxZoom) mapRef.setZoom(maxZoom);
        if (mapRef.getZoom() < minZoom) mapRef.setZoom(minZoom);
    });

    return true;
}

// ─── Component ────────────────────────────────────────────────────────────────

const CommonMap = ({
    hospitalData,
    restriction,
    selectedHospital,
    heatMap,
    tracking,
    detailData,
    selectedIllness,
    reset,
    campData,
}) => {
    const { axios } = useAxios();
    const navigate = useNavigate();

    const [infoWindow, setInfoWindow] = useState(null);
    const [map, setMap] = useState(null);
    const [markerInstance, setMarkerInstance] = useState([]);
    const [hospitalMarker, setHospitalMarker] = useState([]);
    const [campmarker, setCampMarker] = useState([]);
    const [iconMarker, setIconMarker] = useState([]);
    const [illnessMarker, setIllnessMarker] = useState([]);
    const [position] = useState({ lat: 21.1458, lng: 79.0882 });
    const [cataractHeatMap, setcataractHeatMap] = useState(null);
    const [diaHeatMap, setdiaHeatMap] = useState(null);
    const [hypHeatMap, sethypHeatMap] = useState(null);
    const [heatOrHouse, setHeatOrHouse] = useState(null);
    const [polyLines, setpolyLines] = useState(null);
    const [markers, setMarkers] = useState([]);

    const ILLNESS_ICON_MAP = {
        cataract,
        diabetes,
        hypertension,
        cataractDia,
        cataractHyper,
        HyperDia,
        surgery: green,
        cataractHyperDia,
    };

    const mYstyles = [
        { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        { featureType: "road.highway", elementType: "labels", stylers: [{ visibility: "off" }] },
        { featureType: "road.arterial", elementType: "labels", stylers: [{ visibility: "off" }] },
        { featureType: "road.local", elementType: "labels", stylers: [{ visibility: "off" }] },
        { featureType: "water", elementType: "labels", stylers: [{ visibility: "off" }] },
        { featureType: "road", elementType: "labels", stylers: [{ visibility: "off" }] },
    ];

    const WORLD_BOUNDS = { north: 70, south: -65, west: -120, east: 170 };

    const mapOptions = {
        center: { lat: position.lat, lng: position.lng },
        zoom: 4,
        disableDefaultUI: true,
        mapTypeId: 'hybrid',
        zoomControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        styles: mYstyles,
        restriction: { latLngBounds: WORLD_BOUNDS, strictBounds: false },
    };

    // ─── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => { initializeHospitalMap(); }, [reset]);

    useEffect(() => { handleHospital(); }, [hospitalData]);

    useEffect(() => {
        if (campData != null && hospitalData?.length === 0 ||
            campData != undefined && hospitalData?.length === 0) {
            handleCampMap();
        }
    }, [campData]);

    // hospitalHeatMap fires when heatMap prop changes
    useEffect(() => { hospitalHeatMap(); }, [heatMap]);

    useEffect(() => {
        if (heatOrHouse) {
            handleCampMap();
        } else {
            setHeatMap();
        }
    }, [heatOrHouse]);

    useEffect(() => {
        if (tracking !== null) setGeoTrack(tracking);
    }, [tracking]);

    /**
     * Illness icon markers useEffect
     * Replaces old plain-marker rendering with normalizeCoordinate + focusMapOnDenseArea
     */
    useEffect(() => {
        if (!map || !heatMap || !window.google) return;

        // Clear previous illness icon markers
        markers.forEach((m) => m.setMap(null));

        const newMarkers = [];
        const markerPoints = [];

        Object.entries(heatMap).forEach(([illness, data]) => {
            if (!Array.isArray(data?.illness_coords)) return;

            data.illness_coords.forEach(({ latitude, longitude }) => {
                const point = normalizeCoordinate(latitude, longitude);
                if (!point) return;

                const marker = new window.google.maps.Marker({
                    position: point,
                    icon: {
                        url: ILLNESS_ICON_MAP[illness],
                        scaledSize: new window.google.maps.Size(20, 20),
                        anchor: new window.google.maps.Point(10, 10),
                    },
                    map,
                });

                newMarkers.push(marker);
                markerPoints.push(point);   // ← collect for zoom
            });
        });

        setMarkers(newMarkers);

        // Zoom to densest cluster of all rendered illness icons
        if (markerPoints.length > 0) {
            focusMapOnDenseArea(map, window.google, markerPoints, {
                bucketSize: 0.02,
                minClusterPoints: 8,
                minZoom: 10,
                maxZoom: 16,
                padding: 50,
            });
        }
    }, [heatMap, map]);

    // ─── Map init ──────────────────────────────────────────────────────────────

    async function initializeHospitalMap() {
        const google_ = await loader.load();
        setInfoWindow(new google_.maps.InfoWindow());

        const mapField = new google_.maps.Map(
            document.getElementById("hospital"),
            mapOptions
        );

        const div = document.createElement("div");
        div.style.cursor = "pointer";
        div.innerHTML = `<img src=${logo} id='logo' />`;
        div.addEventListener("click", () => {
            window.location = window.location.origin + "/masters";
        });
        mapField.controls[google_.maps.ControlPosition.TOP_LEFT].push(div);

        setMap(mapField);
    }

    // ─── Heatmap (called when heatMap prop changes) ────────────────────────────

    async function hospitalHeatMap() {
        const google_ = await loader.load();

        // Clear old heatmap layers
        if (cataractHeatMap) cataractHeatMap.setMap(null);
        if (diaHeatMap) diaHeatMap.setMap(null);
        if (hypHeatMap) hypHeatMap.setMap(null);

        const cataractGradient = [
            "rgba(9, 144, 255,0)", "rgba(0, 153, 255,1)", "rgba(0, 162, 255,1)",
            "rgba(0, 170, 255,1)", "rgba(0, 178, 255,1)", "rgba(0, 186, 255,1)",
            "rgba(0, 193, 255,1)", "rgba(0, 200, 255,1)", "rgba(0, 207, 255,1)",
            "rgba(8, 214, 255,1)",
        ];
        const diaGradient = [
            "rgba(255, 114, 0,0)", "rgba(255, 130, 0,1)", "rgba(255, 146, 0,1)",
            "rgba(255, 162, 0,1)", "rgba(255, 178, 0,1)", "rgba(255, 194, 0,1)",
            "rgba(255, 209, 0,1)", "rgba(255, 224, 0,1)", "rgba(252, 240, 0,1)",
            "rgba(246, 255, 0,1)",
        ];
        const hypGradient = [
            "rgba(205, 38, 38,0)", "rgba(216, 40, 57,0)", "rgba(225, 44, 76,1)",
            "rgba(233, 51, 94,1)", "rgba(239, 59, 113,1)", "rgba(244, 69, 132,1)",
            "rgba(248, 80, 151,1)", "rgba(250, 91, 170,1)", "rgba(250, 103, 188,1)",
            "rgba(249, 115, 206,1)",
        ];

        const intensity = 15;
        const h_diabetes = { maxIntensity: intensity, data: [] };
        const h_hypertension = { maxIntensity: intensity, data: [] };
        const h_cataract = { maxIntensity: intensity, data: [] };

        // ── collect points for ALL illness types ──
        const markerPoints = [];

        heatMap?.diabetes?.illness_coords?.forEach((coord) => {
            const point = normalizeCoordinate(coord.latitude, coord.longitude);
            if (!point) return;
            h_diabetes.data.push(new google_.maps.LatLng(point.lat, point.lng));
            markerPoints.push(point);
        });
        heatMap?.hypertension?.illness_coords?.forEach((coord) => {
            const point = normalizeCoordinate(coord.latitude, coord.longitude);
            if (!point) return;
            h_hypertension.data.push(new google_.maps.LatLng(point.lat, point.lng));
            markerPoints.push(point);
        });
        heatMap?.cataract?.illness_coords?.forEach((coord) => {
            const point = normalizeCoordinate(coord.latitude, coord.longitude);
            if (!point) return;
            h_cataract.data.push(new google_.maps.LatLng(point.lat, point.lng));
            markerPoints.push(point);
        });

        const dia_heatmap = new google_.maps.visualization.HeatmapLayer({
            data: h_diabetes.data, gradient: diaGradient,
        });
        const hyp_heatmap = new google_.maps.visualization.HeatmapLayer({
            data: h_hypertension.data, gradient: hypGradient,
        });
        const heatmap = new google_.maps.visualization.HeatmapLayer({
            data: h_cataract.data, gradient: cataractGradient,
        });

        setcataractHeatMap(heatmap);
        setdiaHeatMap(dia_heatmap);
        sethypHeatMap(hyp_heatmap);

        dia_heatmap.setMap(map);
        hyp_heatmap.setMap(map);
        heatmap.setMap(map);

        // ── zoom to densest cluster across all three illness types ──
        if (markerPoints.length > 0 && map) {
            focusMapOnDenseArea(map, google_, markerPoints, {
                bucketSize: 0.02,
                minClusterPoints: 8,
                minZoom: 10,
                maxZoom: 16,
                padding: 50,
            });
        }
    }

    // ─── Hospital markers ──────────────────────────────────────────────────────

    async function handleHospital() {
        markers.forEach((marker) => marker.setMap(null));
        markerInstance.forEach((marker) => marker.setMap(null));
        iconMarker.forEach((marker) => marker.setMap(null));
        illnessMarker.forEach((marker) => marker.setMap(null));
        campmarker.forEach((marker) => marker.setMap(null));

        const illnes = document.getElementById('illness');
        if (illnes) illnes.remove();

        const legend = document.getElementById("legend");
        if (legend == null) createLegend();

        if (cataractHeatMap) cataractHeatMap.setMap(null);
        if (diaHeatMap) diaHeatMap.setMap(null);
        if (hypHeatMap) hypHeatMap.setMap(null);

        const google_ = await loader.load();
        const infoWindow = new google_.maps.InfoWindow();
        const circleArray = [];
        const markerPoints = [];

        const hospital_icon = {
            url: redcross,
            size: new google_.maps.Size(50, 50),
            origin: new google_.maps.Point(0, 0),
            anchor: new google_.maps.Point(25, 25),
        };
        const camp_icon = {
            url: camp_png,
            size: new google_.maps.Size(50, 50),
            origin: new google_.maps.Point(0, 0),
            anchor: new google_.maps.Point(25, 25),
        };

        markerInstance.forEach((marker) => marker.setMap(null));
        const markerInfoWindow = new google_.maps.InfoWindow();

        function resizeMarkers(map) {
            markerInstance.forEach((marker) => {
                const pixelSizeAtZoom0 = 5;
                const maxPixelSize = 20;
                const zoom = map.getZoom();
                let relativePixelSize = Math.round(pixelSizeAtZoom0 * Math.pow(2, zoom));
                if (relativePixelSize > maxPixelSize) relativePixelSize = maxPixelSize;
                marker.setIcon({
                    url: marker.getIcon().url,
                    size: null, origin: null, anchor: null,
                    scaledSize: new google_.maps.Size(relativePixelSize, relativePixelSize),
                });
            });
        }
        google_.maps.event.addListener(map, 'zoom_changed', () => resizeMarkers(map));

        hospitalData?.map((hospital) => {
            if (hospital?.hospital_id === "1") return;

            const hospitalPoint = normalizeCoordinate(hospital.latitude, hospital.longitude);
            if (!hospitalPoint) return;

            let hMarker = new google_.maps.Marker({
                position: new google_.maps.LatLng(hospitalPoint.lat, hospitalPoint.lng),
                map,
                icon: hospital_icon,
            });

            markerInstance.push(hMarker);
            markerPoints.push({ lat: hospitalPoint.lat, lng: hospitalPoint.lng });

            const circle = new google_.maps.Circle({
                map,
                center: hMarker.getPosition(),
                radius: hospitalData.radius,
                strokeColor: '#FF0000',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: '#FF0000',
                fillOpacity: 0.35,
            });
            circleArray.push(circle);
            circle.bindTo('center', hMarker, 'position');

            if (hospitalData?.length === 1) {
                const fetchGisCamp = async () => {
                    await axios.post('getGISCampList', {
                        hospital_id: [hospital.hospital_id],
                    }).then((res) => {
                        const camp_details = res.data.result.result;
                        const campPoints = [];

                        camp_details?.map((camps) => {
                            camps?.campList?.map((cam) => {
                                const campPoint = normalizeCoordinate(cam.current_latitude, cam.current_longitude);
                                if (!campPoint) return;

                                let campMarker = new google_.maps.Marker({
                                    position: new google_.maps.LatLng(campPoint.lat, campPoint.lng),
                                    map,
                                    icon: camp_icon,
                                });
                                campMarker.addListener("click", async () => {
                                    addHosueholdsViaClick({ camp_id: [cam?.camp_id] });
                                });
                                markerInstance.push(campMarker);
                                campPoints.push({ lat: campPoint.lat, lng: campPoint.lng });
                                markerPoints.push({ lat: campPoint.lat, lng: campPoint.lng });
                            });
                        });

                        focusMapOnDenseArea(map, google_, markerPoints, {
                            bucketSize: 0.03,
                            minClusterPoints: 8,
                            minZoom: 10,
                            maxZoom: 15,
                            padding: 55,
                        });
                    });
                };
                fetchGisCamp();
            } else {
                hMarker.addListener('mouseover', () => {
                    markerInfoWindow.close();
                    markerInfoWindow.setContent(`
                        <div>
                            <img width='100%' height='100' src=${logo} />
                            <h3 class='mar'>Hospital Name</h3>
                            <span>${hospital?.hospital_name}</span>
                        </div>`);
                    markerInfoWindow.open(map, hMarker);
                });

                hMarker.addListener('click', () => {
                    markerInstance.forEach((marker) => marker.setMap(null));
                    addCampviaClick(hMarker, hospital);
                    selectedHospital({ hospital_id: [hospital.hospital_id] });
                });
            }
        });

        // Zoom to all hospital markers
        if (markerPoints.length > 0 && map) {
            focusMapOnDenseArea(map, google_, markerPoints, {
                bucketSize: 0.03,
                minClusterPoints: 8,
                minZoom: 10,
                maxZoom: 15,
                padding: 55,
            });
        }
    }

    // ─── Camp markers ──────────────────────────────────────────────────────────

    async function handleCampMap() {
        markerInstance.forEach((marker) => marker.setMap(null));

        const google_ = await loader.load();
        illnessMarker.forEach((marker) => marker.setMap(null));
        campmarker.forEach((marker) => marker.setMap(null));

        const infoWindow_ = new google_.maps.InfoWindow();
        setInfoWindow(infoWindow_);

        if (cataractHeatMap) cataractHeatMap.setMap(null);
        if (diaHeatMap) diaHeatMap.setMap(null);
        if (hypHeatMap) hypHeatMap.setMap(null);

        const illnes = document.getElementById('illness');
        if (illnes == null) createElement();
        const legend = document.getElementById("legend");
        if (legend == null) createLegend();

        const camp_icon = {
            url: camp_png,
            size: new google_.maps.Size(50, 50),
            origin: new google_.maps.Point(0, 0),
            anchor: new google_.maps.Point(25, 25),
        };
        const hosuehold = {
            url: hosueholds,
            size: new google_.maps.Size(50, 50),
            origin: new google_.maps.Point(0, 0),
            anchor: new google_.maps.Point(25, 25),
        };

        function resizeMarkers(map) {
            markerInstance.forEach((marker) => {
                const pixelSizeAtZoom0 = 5;
                const maxPixelSize = 30;
                const zoom = map.getZoom();
                let relativePixelSize = Math.round(pixelSizeAtZoom0 * Math.pow(2, zoom));
                if (relativePixelSize > maxPixelSize) relativePixelSize = maxPixelSize;
                marker.setIcon({
                    url: marker.getIcon().url,
                    size: null, origin: null, anchor: null,
                    scaledSize: new google_.maps.Size(relativePixelSize, relativePixelSize),
                });
            });
        }
        google_.maps.event.addListener(map, 'zoom_changed', () => resizeMarkers(map));

        const markerInfoWindow = new google_.maps.InfoWindow();
        const newmarker = [];
        const markerPoints = [];

        for (let i = 0; i <= Object.keys(campData).length - 1; i++) {
            let iItem = campData[Object.keys(campData)[i]];
            if (!iItem.coords) continue;

            for (let j = 0; j <= Object.keys(iItem.coords).length - 1; j++) {
                let jItem = Object.keys(iItem.coords)[j];
                if (!iItem.coords?.camp_coords || iItem.coords.camp_coords.length === 0) continue;

                for (let k = 0; k <= iItem.coords[jItem].length - 1; k++) {
                    let kItem = iItem.coords[jItem][k];
                    const point = normalizeCoordinate(kItem.current_latitude, kItem.current_longitude);
                    if (!point) continue;

                    let marker = new google_.maps.Marker({
                        position: new google_.maps.LatLng(point.lat, point.lng),
                        map,
                        icon: jItem === 'camp_coords' ? camp_icon : hosuehold,
                    });

                    if (jItem === 'household_coords') {
                        marker.addListener("click", async () => {
                            const data = campData[Object.keys(campData)[i]].detail?.household_detail
                                ?.find((x) => x.household_id === kItem.household_id);
                            detailData(data, 'household');
                            markerInfoWindow.close();
                            markerInfoWindow.setContent(`
                                <div>
                                    <h3>Patient Name : ${data?.name}</h3>
                                    <h4>Patient Age : ${data?.age}</h4>
                                </div>`);
                            markerInfoWindow.open(map, marker);
                        });
                    } else {
                        marker.addListener("click", async () => {
                            const data = campData[Object.keys(campData)[i]].detail?.camp_detail
                                ?.find((x) => x.camp_id === kItem.camp_id);
                            detailData(data, 'camp');
                            markerInfoWindow.close();
                            markerInfoWindow.setContent(`
                                <div>
                                    <img width='100%' height='100' src=${logo} />
                                    <h3 class='mar'>Camp place : ${data?.camp_place}</h3>
                                </div>`);
                            markerInfoWindow.open(map, marker);
                        });
                    }

                    newmarker.push(marker);
                    markerPoints.push(point);
                }
            }
        }

        setCampMarker(newmarker);

        // Zoom to densest cluster of camp + household markers
        if (markerPoints.length > 0 && map) {
            focusMapOnDenseArea(map, google_, markerPoints, {
                bucketSize: 0.02,
                minClusterPoints: 10,
                minZoom: 12,
                maxZoom: 16,
                padding: 50,
            });
        }
    }

    // ─── Tracking ──────────────────────────────────────────────────────────────

    async function setGeoTrack(selectedDate) {
        if (cataractHeatMap) cataractHeatMap.setMap(null);
        if (diaHeatMap) diaHeatMap.setMap(null);
        if (hypHeatMap) hypHeatMap.setMap(null);

        markerInstance.forEach((marker) => marker.setMap(null));
        campmarker.forEach((marker) => marker.setMap(null));
        illnessMarker.forEach((marker) => marker.setMap(null));

        const illnes = document.getElementById('illness');
        if (illnes) illnes.remove();

        const google_ = await loader.load();

        markers?.forEach((marker) => marker.setMap(null));
        if (polyLines) polyLines.setMap(null);
        iconMarker?.forEach((marker) => marker.setMap(null));

        const tracking_icon_cfg = {
            url: tracking_icon,
            size: new google_.maps.Size(50, 50),
            origin: new google_.maps.Point(0, 0),
            anchor: new google_.maps.Point(25, 25),
        };
        const hosuehold_icon = {
            url: hosueholds,
            size: new google_.maps.Size(50, 50),
            origin: new google_.maps.Point(0, 0),
            anchor: new google_.maps.Point(25, 25),
        };
        const punchin = {
            url: shiftin,
            size: new google_.maps.Size(50, 50),
            origin: new google_.maps.Point(0, 0),
            anchor: new google_.maps.Point(25, 25),
        };
        const punchout = {
            url: shiftout,
            size: new google_.maps.Size(50, 50),
            origin: new google_.maps.Point(0, 0),
            anchor: new google_.maps.Point(25, 25),
        };

        // Normalize all tracking coordinates
        const rawCoords = Array.isArray(selectedDate?.coords) ? selectedDate.coords : [];
        const coords = rawCoords
            .map((p) => {
                const normalized = normalizeCoordinate(p?.current_latitude, p?.current_longitude);
                if (!normalized) return null;
                return { ...p, current_latitude: normalized.lat, current_longitude: normalized.lng };
            })
            .filter(Boolean);

        if (coords.length === 0) return;

        const coordinates = coords.map(
            (i) => new google_.maps.LatLng(i.current_latitude, i.current_longitude)
        );

        const polyline = new google_.maps.Polyline({
            path: coordinates,
            geodesic: true,
            strokeColor: '#ff7300',
            strokeOpacity: 1.0,
            strokeWeight: 2,
        });
        polyline.setMap(map);
        setpolyLines(polyline);

        const land_marker = [];
        coords.forEach((i) => {
            const event = i.event.replace(/ /g, "").toLowerCase();
            if (event !== 'tracking') {
                const newMarker = new google_.maps.Marker({
                    position: new google_.maps.LatLng(i.current_latitude, i.current_longitude),
                    map,
                    icon:
                        event === 'household' ? hosuehold_icon
                        : event === 'punchin' ? punchin
                        : event === 'punchout' ? punchout
                        : tracking_icon_cfg,
                });
                land_marker.push(newMarker);
            }
        });
        setIconMarker(land_marker);

        const normalizedPoints = coords.map((p) => ({
            lat: p.current_latitude,
            lng: p.current_longitude,
        }));

        // Zoom to densest cluster of the tracking path
        focusMapOnDenseArea(map, google_, normalizedPoints, {
            bucketSize: 0.015,
            minClusterPoints: 10,
            minZoom: 13,
            maxZoom: 17,
            padding: 40,
        });
    }

    // ─── Illness icon view (toggle) ────────────────────────────────────────────

    /**
     * setHeatMap — called when heatOrHouse is toggled to "show illness icons"
     * Shows coloured dot markers + heatmap layers, then zooms to densest cluster.
     */
    async function setHeatMap() {
        campmarker.forEach((marker) => marker.setMap(null));
        illnessMarker.forEach((marker) => marker.setMap(null));

        const legend = document.getElementById("legend");
        if (legend == null) createLegend();

        const google_ = await loader.load();

        if (cataractHeatMap) cataractHeatMap.setMap(null);
        if (diaHeatMap) diaHeatMap.setMap(null);
        if (hypHeatMap) hypHeatMap.setMap(null);

        const cataractGradient = [
            "rgba(9, 144, 255,0)", "rgba(0, 153, 255,1)", "rgba(0, 162, 255,1)",
            "rgba(0, 170, 255,1)", "rgba(0, 178, 255,1)", "rgba(0, 186, 255,1)",
            "rgba(0, 193, 255,1)", "rgba(0, 200, 255,1)", "rgba(0, 207, 255,1)",
            "rgba(8, 214, 255,1)",
        ];
        const diaGradient = [
            "rgba(255, 114, 0,0)", "rgba(255, 130, 0,1)", "rgba(255, 146, 0,1)",
            "rgba(255, 162, 0,1)", "rgba(255, 178, 0,1)", "rgba(255, 194, 0,1)",
            "rgba(255, 209, 0,1)", "rgba(255, 224, 0,1)", "rgba(252, 240, 0,1)",
            "rgba(246, 255, 0,1)",
        ];
        const hypGradient = [
            "rgba(205, 38, 38,0)", "rgba(216, 40, 57,0)", "rgba(225, 44, 76,1)",
            "rgba(233, 51, 94,1)", "rgba(239, 59, 113,1)", "rgba(244, 69, 132,1)",
            "rgba(248, 80, 151,1)", "rgba(250, 91, 170,1)", "rgba(250, 103, 188,1)",
            "rgba(249, 115, 206,1)",
        ];

        const intensity = 15;
        const h_diabetes = { maxIntensity: intensity, data: [] };
        const h_hypertension = { maxIntensity: intensity, data: [] };
        const h_cataract = { maxIntensity: intensity, data: [] };

        const iconConfigs = {
            cataract:        { url: lightblue },
            surgerydonedetails: { url: green },
            diabetes:        { url: red },
            hypertension:    { url: orange },
            cataractDia:     { url: violet },
            cataractHyper:   { url: brown },
            HyperDia:        { url: purple },
            cataractHyperDia:{ url: yellow },
        };

        const allMarks = [];
        const markerPoints = [];

        // Render a dot marker for every illness type in one loop
        Object.entries(iconConfigs).forEach(([key, cfg]) => {
            heatMap?.[key]?.illness_coords?.forEach((coord) => {
                const point = normalizeCoordinate(coord.latitude, coord.longitude);
                if (!point) return;

                const marker = new google_.maps.Marker({
                    position: new google_.maps.LatLng(point.lat, point.lng),
                    map,
                    icon: {
                        url: cfg.url,
                        size: new google_.maps.Size(20, 20),
                        origin: new google_.maps.Point(0, 0),
                        anchor: new google_.maps.Point(25, 25),
                    },
                });
                allMarks.push(marker);
                markerPoints.push(point);
            });
        });

        setIllnessMarker(allMarks);

        // Build heatmap data (only cataract / diabetes / hypertension have heatmaps)
        heatMap?.diabetes?.illness_coords?.forEach((coord) => {
            const point = normalizeCoordinate(coord.latitude, coord.longitude);
            if (!point) return;
            h_diabetes.data.push(new google_.maps.LatLng(point.lat, point.lng));
        });
        heatMap?.hypertension?.illness_coords?.forEach((coord) => {
            const point = normalizeCoordinate(coord.latitude, coord.longitude);
            if (!point) return;
            h_hypertension.data.push(new google_.maps.LatLng(point.lat, point.lng));
        });
        heatMap?.cataract?.illness_coords?.forEach((coord) => {
            const point = normalizeCoordinate(coord.latitude, coord.longitude);
            if (!point) return;
            h_cataract.data.push(new google_.maps.LatLng(point.lat, point.lng));
        });

        const dia_heatmap = new google_.maps.visualization.HeatmapLayer({
            data: h_diabetes.data, gradient: diaGradient,
        });
        const hyp_heatmap = new google_.maps.visualization.HeatmapLayer({
            data: h_hypertension.data, gradient: hypGradient,
        });
        const heatmap = new google_.maps.visualization.HeatmapLayer({
            data: h_cataract.data, gradient: cataractGradient,
        });

        setcataractHeatMap(heatmap);
        setdiaHeatMap(dia_heatmap);
        sethypHeatMap(hyp_heatmap);

        dia_heatmap.setMap(map);
        hyp_heatmap.setMap(map);
        heatmap.setMap(map);

        // Zoom to densest cluster across ALL illness markers
        if (markerPoints.length > 0 && map) {
            focusMapOnDenseArea(map, google_, markerPoints, {
                bucketSize: 0.02,
                minClusterPoints: 12,
                minZoom: 12,
                maxZoom: 16,
                padding: 50,
            });
        }
    }

    // ─── Single illness heatmap ────────────────────────────────────────────────

    /**
     * selectedHeatMap — zooms to whichever single illness is selected.
     */
    async function selectedHeatMap(illness) {
        const google_ = await loader.load();

        cataractHeatMap?.setMap(null);
        diaHeatMap?.setMap(null);
        hypHeatMap?.setMap(null);

        const cataractGradient = [
            "rgba(9, 144, 255,0)", "rgba(0, 153, 255,1)", "rgba(0, 162, 255,1)",
            "rgba(0, 170, 255,1)", "rgba(0, 178, 255,1)", "rgba(0, 186, 255,1)",
            "rgba(0, 193, 255,1)", "rgba(0, 200, 255,1)", "rgba(0, 207, 255,1)",
            "rgba(8, 214, 255,1)",
        ];
        const diaGradient = [
            "rgba(255, 114, 0,0)", "rgba(255, 130, 0,1)", "rgba(255, 146, 0,1)",
            "rgba(255, 162, 0,1)", "rgba(255, 178, 0,1)", "rgba(255, 194, 0,1)",
            "rgba(255, 209, 0,1)", "rgba(255, 224, 0,1)", "rgba(252, 240, 0,1)",
            "rgba(246, 255, 0,1)",
        ];
        const hypGradient = [
            "rgba(205, 38, 38,0)", "rgba(216, 40, 57,0)", "rgba(225, 44, 76,1)",
            "rgba(233, 51, 94,1)", "rgba(239, 59, 113,1)", "rgba(244, 69, 132,1)",
            "rgba(248, 80, 151,1)", "rgba(250, 91, 170,1)", "rgba(250, 103, 188,1)",
            "rgba(249, 115, 206,1)",
        ];

        const rawCoords =
            illness === 'cataract'
                ? heatMap[illness]?.cataract_coords
                : heatMap[illness]?.illness_coords;

        const heatData = [];
        const markerPoints = [];

        (rawCoords || []).forEach((coord) => {
            const point = normalizeCoordinate(coord.latitude, coord.longitude);
            if (!point) return;
            heatData.push(new google_.maps.LatLng(point.lat, point.lng));
            markerPoints.push(point);
        });

        const gradient =
            illness === 'cataract' ? cataractGradient
            : illness === 'hypertension' ? hypGradient
            : illness === 'diabetes' ? diaGradient
            : null;

        const heat = new google_.maps.visualization.HeatmapLayer({
            data: heatData,
            gradient,
        });

        if (illness === 'cataract') {
            setcataractHeatMap(heat);
        } else if (illness === 'hypertension') {
            sethypHeatMap(heat);
        } else if (illness === 'diabetes') {
            setdiaHeatMap(heat);
        }
        heat.setMap(map);

        // Zoom to densest cluster of this illness's coordinates
        if (markerPoints.length > 0 && map) {
            focusMapOnDenseArea(map, google_, markerPoints, {
                bucketSize: 0.02,
                minClusterPoints: 8,
                minZoom: 10,
                maxZoom: 16,
                padding: 50,
            });
        }
    }

    // ─── Camp via hospital click ───────────────────────────────────────────────

    async function addCampviaClick(hospitalMarker, hospital) {
        const newMarkers = [];
        const google_ = await loader.load();
        const markerInfoWindow = new google_.maps.InfoWindow();

        const camp_icon = {
            url: camp_png,
            size: new google_.maps.Size(50, 50),
            origin: new google_.maps.Point(0, 0),
            anchor: new google_.maps.Point(25, 25),
        };
        const hospital_icon = {
            url: redcross,
            size: new google_.maps.Size(50, 50),
            origin: new google_.maps.Point(0, 0),
            anchor: new google_.maps.Point(25, 25),
        };

        await axios.post('getGISCampList', {
            hospital_id: [hospital.hospital_id],
        }).then((res) => {
            const camp_details = res.data.result.result;
            const markerPoints = [];

            const hospitalMk = new google_.maps.Marker({
                position: hospitalMarker.getPosition(),
                map,
                icon: hospital_icon,
            });
            newMarkers.push(hospitalMk);
            markerPoints.push({
                lat: hospitalMarker.getPosition().lat(),
                lng: hospitalMarker.getPosition().lng(),
            });

            camp_details?.forEach((camp) => {
                camp?.campList?.forEach((cam) => {
                    const point = normalizeCoordinate(cam.current_latitude, cam.current_longitude);
                    if (!point) return;

                    let campMarker = new google_.maps.Marker({
                        position: new google_.maps.LatLng(point.lat, point.lng),
                        icon: camp_icon,
                    });
                    campMarker.addListener("click", async () => {
                        addHosueholdsViaClick({ camp_id: [cam?.camp_id] });
                    });
                    newMarkers.push(campMarker);
                    markerPoints.push(point);
                });
            });

            setCampMarker(newMarkers);
            newMarkers.forEach((marker) => marker.setMap(map));
            setMarkerInstance(newMarkers);

            focusMapOnDenseArea(map, google_, markerPoints, {
                bucketSize: 0.02,
                minClusterPoints: 8,
                minZoom: 12,
                maxZoom: 16,
                padding: 45,
            });
        });
    }

    // ─── Households via camp click ─────────────────────────────────────────────

    async function addHosueholdsViaClick(data) {
        const google_ = await loader.load();
        const markerInfoWindow = new google_.maps.InfoWindow();

        const camp_icon = {
            url: camp_png,
            size: new google_.maps.Size(50, 50),
            origin: new google_.maps.Point(0, 0),
            anchor: new google_.maps.Point(25, 25),
        };
        const hosuehold = {
            url: hosueholds,
            size: new google_.maps.Size(50, 50),
            origin: new google_.maps.Point(0, 0),
            anchor: new google_.maps.Point(25, 25),
        };

        await axios.post('getCampSurveyList', data).then((res) => {
            const data_ = res.data.result;
            const newcampMarker = [];
            const markerPoints = [];

            markerInstance.forEach((marker) => marker.setMap(null));
            campmarker.forEach((marker) => marker.setMap(null));

            for (let i = 0; i <= Object.keys(data_).length - 1; i++) {
                let iItem = data_[Object.keys(data_)[i]];
                if (!iItem.coords) continue;

                for (let j = 0; j <= Object.keys(iItem.coords).length - 1; j++) {
                    let jItem = Object.keys(iItem.coords)[j];
                    if (!iItem.coords?.camp_coords || iItem.coords.camp_coords.length === 0) continue;

                    for (let k = 0; k <= iItem.coords[jItem].length - 1; k++) {
                        let kItem = iItem.coords[jItem][k];
                        const point = normalizeCoordinate(kItem.current_latitude, kItem.current_longitude);
                        if (!point) continue;

                        let marker = new google_.maps.Marker({
                            position: new google_.maps.LatLng(point.lat, point.lng),
                            map,
                            icon: jItem === 'camp_coords' ? camp_icon : hosuehold,
                        });

                        if (jItem === 'household_coords') {
                            marker.addListener("click", async () => {
                                const d = data_[Object.keys(data_)[i]].detail?.household_detail
                                    ?.find((x) => x.household_id === kItem.household_id);
                                detailData(d, 'household');
                                markerInfoWindow.close();
                                markerInfoWindow.setContent(`
                                    <div>
                                        <h3>Patient Name : ${d?.name}</h3>
                                        <h4>Patient Age : ${d?.age}</h4>
                                    </div>`);
                                markerInfoWindow.open(map, marker);
                            });
                        } else {
                            marker.addListener("click", async () => {
                                const d = data_[Object.keys(data_)[i]].detail?.camp_detail
                                    ?.find((x) => x.camp_id === kItem.camp_id);
                                detailData(d, 'camp');
                                markerInfoWindow.close();
                                markerInfoWindow.setContent(`
                                    <div>
                                        <img width='100%' height='100' src=${logo} />
                                        <h3 class='mar'>Camp place : ${d?.camp_place}</h3>
                                    </div>`);
                                markerInfoWindow.open(map, marker);
                            });
                        }

                        newcampMarker.push(marker);
                        markerPoints.push(point);
                    }
                }
            }

            focusMapOnDenseArea(map, google_, markerPoints, {
                bucketSize: 0.015,
                minClusterPoints: 10,
                minZoom: 13,
                maxZoom: 17,
                padding: 40,
            });
        });
    }

    // ─── UI controls ──────────────────────────────────────────────────────────

    async function createElement() {
        const google_ = await loader.load();
        const controlDiv = document.createElement("div");
        controlDiv.setAttribute("id", "illness");
        controlDiv.style.padding = "5px";
        controlDiv.style.backgroundColor = "#ff7100";
        controlDiv.style.border = "1px solid #ffffff";

        const controlUI = document.createElement("div");
        controlUI.style.cursor = "pointer";
        controlUI.style.textAlign = "center";
        controlUI.style.width = "100px";
        controlUI.style.fontWeight = "bold";
        controlUI.style.color = "#ffffff";
        controlUI.innerHTML = "Show Illness";

        let showMarkers = true;
        controlUI.addEventListener("click", () => {
            showMarkers = !showMarkers;
            if (showMarkers) {
                setHeatOrHouse(true);
                controlUI.innerHTML = "Show Illness";
            } else {
                setHeatOrHouse(false);
                controlUI.innerHTML = "Show HouseHold";
            }
        });

        controlDiv.appendChild(controlUI);
        map.controls[google_.maps.ControlPosition.TOP_RIGHT].push(controlDiv);
    }

    async function createLegend() {
        const google_ = await loader.load();

        const icons = {
            cataract:  { name: "Cataract",                      icon: cataract },
            surgery:   { name: "Cataract Surgery Done",          icon: green },
            diabetes:  { name: "Diabetes",                       icon: diabetes },
            hypertension: { name: "Hypertension",               icon: hypertension },
            candd:     { name: "Cataract & Diabetes",            icon: cataractDia },
            candh:     { name: "Cataract & Hypertension",        icon: cataractHyper },
            hyanddia:  { name: "Hypertension & Diabetes",        icon: HyperDia },
            chd:       { name: "Cataract & Hyper & Diabetes",    icon: cataractHyperDia },
        };

        const legend = document.createElement("div");
        legend.setAttribute("id", "legend");
        legend.style.padding = "5px";
        legend.style.backgroundColor = "#ffffff";
        legend.style.border = "1px solid #ffffff";

        for (const key in icons) {
            const { name, icon } = icons[key];
            const div = document.createElement("div");
            div.innerHTML = `<div><img src="${icon}" width="24" height="24"> <h6>${name}</h6></div>`;
            legend.appendChild(div);
        }

        map.controls[google_.maps.ControlPosition.RIGHT_CENTER].push(legend);
    }

    // ─── Render ────────────────────────────────────────────────────────────────

    return (
        <>
            <div id='hospital' className='newmaps_'></div>
        </>
    );
};

export default CommonMap;